/**
 * Web Push на клиенте: разрешение, подписка через PushManager и передача её
 * серверу. Работает только в браузерной версии с service worker; на iPhone —
 * лишь у установленного на экран PWA (ограничение самого iOS).
 */
import { api } from './api'

/** Поддерживает ли среда web-push вообще. */
export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

/** base64url ключ VAPID → Uint8Array для applicationServerKey. */
function toKeyArray(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4)
  const base64 = (base64url + pad).replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(base64)
  const arr = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i)
  return arr
}

/** Разрешение уже спрашивали (неважно, чем кончилось)? */
export function pushAsked(): boolean {
  return pushSupported() && Notification.permission !== 'default'
}

/**
 * Спрашивает разрешение, подписывается и шлёт подписку серверу.
 * Возвращает true, если подписка оформлена. false — если не поддерживается или
 * человек не дал разрешения.
 */
export async function enablePush(): Promise<{ ok: boolean; bonus: number; stars?: number }> {
  if (!pushSupported()) return { ok: false, bonus: 0 }

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') return { ok: false, bonus: 0 }

  const reg = await navigator.serviceWorker.ready
  const { key } = await api.pushKey()
  if (!key) return { ok: false, bonus: 0 }

  const existing = await reg.pushManager.getSubscription()
  const sub =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true, // без этого iOS/Chrome не дадут подписку
      applicationServerKey: toKeyArray(key),
    }))

  // toJSON() отдаёт { endpoint, keys: { p256dh, auth } } — ровно что ждёт сервер.
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } }
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return { ok: false, bonus: 0 }
  const res = await api.pushSubscribe({
    endpoint: json.endpoint,
    keys: { p256dh: json.keys.p256dh, auth: json.keys.auth },
  })
  // Награду начисляет сервер прямо тут, увидев подписку. Возвращаем её наверх,
  // чтобы экран сказал об этом словами: молча начисленные звёзды человек не
  // заметит, а обещали мы их вслух.
  return { ok: true, bonus: res.bonus ?? 0, stars: res.stars }
}

/** Отписывается локально и на сервере. */
export async function disablePush(): Promise<void> {
  if (!pushSupported()) return
  const reg = await navigator.serviceWorker.ready
  const sub = await reg.pushManager.getSubscription()
  if (!sub) return
  try {
    await api.pushUnsubscribe(sub.endpoint)
  } catch {
    /* сеть подождёт — локально всё равно отписываемся */
  }
  await sub.unsubscribe()
}

/**
 * Спросить разрешение сразу после установки приложения.
 *
 * Момент выбран не случайно: человек только что сам согласился поставить
 * приложение на экран, и вопрос про напоминания читается как продолжение того
 * же разговора, а не как всплывшее из ниоткуда требование. Позже — уже холодно.
 *
 * Отказ не повторяем: браузер запоминает `denied` навсегда, и второй запрос
 * ничего не изменит. Включить можно в профиле.
 */
export async function afterInstallAskPush(): Promise<boolean> {
  if (!pushSupported() || pushAsked()) return false
  try {
    const { ok } = await enablePush()
    if (ok) {
      await api.setNotify(true)
      // Профиль в хранилище правит сам экран; здесь достаточно события —
      // на него подписан store и переключатель в профиле встанет сам.
      window.dispatchEvent(new CustomEvent('sirr-notify-on'))
    }
    return ok
  } catch {
    // Отказ или недоступность — не повод показывать ошибку: уведомления
    // необязательны, а человек только что установил приложение.
    return false
  }
}
